<script lang="ts">
  import { browser } from '$app/environment';
  import { page } from '$app/stores';

  import { captureException } from '@sentry/sveltekit';

  import type { ArtAssetType } from '@do/models';
  import { useWallet, useWalletModal, useConnect } from '@do/svelte-web3-manager';

  import { Button } from '@shared/ui/buttons/button';
  import { ButtonRainbow } from '@shared/ui/buttons/button-rainbow';
  import { type IconNames } from '@shared/ui/icon';
  import { cn } from '@shared/utils';

  import {
    PurchaseState,
    handlePurchaseError,
    handlePurchaseSuccess,
    purchaseModel,
    requestApproval,
    texts,
  } from '@entities/purchase';
  import { walletLibs } from '@entities/wallet';

  import { artworkPurchaseInfo, purchasePrice } from '@features/purchase/purchase-artwork';
  import type { SaleOrder } from '@features/purchase/purchase-artwork/types';
  import Price from '@features/purchase/purchase-artwork/ui/price.svelte';

  import PurchaseHint from './purchase-hint.svelte';

  export let price: number | null = null;
  export let saleOrder: SaleOrder | null | undefined = undefined;
  export let tokenId: number | null = null;
  export let artworkName: string;
  export let artist: string;
  export let artAssetType: ArtAssetType;

  const { connect } = useConnect();

  const wallet = useWallet();
  const web3Modal = useWalletModal();

  let buttonText: string;
  let isLoading: boolean;
  let icon: IconNames | undefined = undefined;
  let isConfirmChecked = false;
  $: isConfirmError = '';

  $: isProcessing =
    $purchaseModel.state === PurchaseState.Success ||
    $purchaseModel.state === PurchaseState.Pending ||
    $purchaseModel.state === PurchaseState.InProgress ||
    $purchaseModel.state === PurchaseState.Approval ||
    $purchaseModel.state === PurchaseState.ConnectWallet ||
    $purchaseModel.state === PurchaseState.SwitchChain;
  $: buttonText = texts[$purchaseModel.state];
  $: isLoading = $purchaseModel.state === PurchaseState.InProgress || $purchaseModel.state === PurchaseState.Pending;
  $: icon = $purchaseModel.state === PurchaseState.Error ? 'retry' : undefined;

  let isChecked = false;

  $: if ($web3Modal !== null) {
    if (!isChecked) checkIsSold();
  }

  const checkIsSold = async () => {
    if (!tokenId) {
      console.error('No token ID provided');
      return false;
    }

    if (browser) {
      const owner = await purchaseModel.getOwner(tokenId, artAssetType);

      console.log({ owner });

      if (owner) {
        // if we have  owner it means that token is already sold but our back-end is behind and does not know about it yet
        purchaseModel.set({ state: PurchaseState.Success });

        return true;
      }
    }

    return false;
  };

  function triggerGtmEvent(eventName: string) {
    if (window && window?.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        userType: $wallet ? 'logged in' : 'logged off',
        artName: artworkName,
        artistName: artist,
        placement: $page.url.href,
      });
    }
  }

  const handleCheckStatuses = () => {
    if (!$artworkPurchaseInfo && $purchaseModel.state !== PurchaseState.Error) {
      artworkPurchaseInfo.toggle();

      return false;
    }

    if (!isConfirmChecked && $purchaseModel.state === PurchaseState.Default) {
      isConfirmError = 'Please confirm that you have read and agree to the platform rules';

      return false;
    }

    isConfirmError = '';

    if (!$wallet) {
      connect({
        onConnecting: () => {
          purchaseModel.set({ state: PurchaseState.ConnectWallet });
          artworkPurchaseInfo.toggle();
        },
        onExit: () => {
          purchaseModel.set({ state: PurchaseState.Default });
          artworkPurchaseInfo.toggle();
        },
      });

      triggerGtmEvent('placeBid');

      return false;
    }

    if (!saleOrder) {
      console.error('Invalid sale order data received');
      return false;
    }

    return true;
  };

  const handleClick = async () => {
    const isReady = handleCheckStatuses();
    const isSold = await checkIsSold();

    if (!isReady || isSold) {
      return;
    }

    if (!$wallet?.address) {
      console.error('Missing wallet address. Aborting purchase.');

      return;
    }

    if (!saleOrder?.id) {
      console.error('Invalid sale order – missing id');

      return;
    }

    try {
      purchaseModel.set({ state: PurchaseState.Approval });

      triggerGtmEvent('placeBid');

      console.log('switch chain was successful');

      artworkPurchaseInfo.hide();

      const approvalResponse = await requestApproval(saleOrder.id, $wallet.address);

      console.log('approvalResponse', approvalResponse);

      const totalPriceMicros = BigInt(approvalResponse.price) + BigInt(approvalResponse.fee);
      const purchasePriceUsdc = Number(totalPriceMicros) / 10 ** 6;

      purchasePrice.set(purchasePriceUsdc);

      await purchaseModel.approveERC20Transfer(
        approvalResponse.currency.address,
        totalPriceMicros,
        $wallet?.address as `0x${string}`,
        artAssetType,
      );

      await purchaseModel.buy(approvalResponse, artAssetType);

      purchaseModel.set({ state: PurchaseState.Success });

      handlePurchaseSuccess();
      triggerGtmEvent('artSold');
    } catch (error: any) {
      captureException(error, { extra: { saleOrder, wallet: $wallet } });
      const message = walletLibs.parseBlockchainError(error);
      purchaseModel.set({ state: PurchaseState.Error });
      handlePurchaseError(message);
    }
  };
</script>

<div class="shadow-wallet-sidebar dark:bg-alt-dark rounded-lg p-5">
  {#if price && saleOrder}
    <div class="mb-5">
      <Price {price} {saleOrder} bind:checked="{isConfirmChecked}" error="{isConfirmError}" />
    </div>
  {/if}

  <div>
    {#if isProcessing}
      <ButtonRainbow isLoading>{buttonText}</ButtonRainbow>
    {:else}
      <div class="{cn({ 'mb-3': $purchaseModel.state !== PurchaseState.Error })}">
        <Button
          on:click="{handleClick}"
          class="dark:disabled:!bg-dark-200 dark:disabled:text-dark-600 rounded-lg"
          loading="{isLoading}"
          disabled="{isLoading || ($artworkPurchaseInfo && $purchaseModel.state === PurchaseState.InsufficientFunds)}"
          iconName="{icon}"
        >
          {buttonText}
        </Button>
      </div>

      {#if $purchaseModel.state === PurchaseState.Default || $purchaseModel.state === PurchaseState.InsufficientFunds}
        <PurchaseHint />
      {/if}
    {/if}
  </div>
</div>
